/**
 * General utilities for the EZHaul application.
 */

/**
 * Joins class names, filtering out falsy values.
 * Works with CSS Modules — pass `styles.className` directly.
 */
export function cn(
  ...classes: (string | undefined | null | false)[]
): string {
  return classes.filter(Boolean).join(' ');
}

/**
 * Formats a Date using Intl.DateTimeFormat.
 * Defaults to the user's locale if none is provided.
 */
export function formatDate(date: Date, locale?: string): string {
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(date);
}

/**
 * Returns a human-readable relative time string.
 * E.g. "just now", "5 min ago", "2 hours ago", "3 days ago".
 */
export function formatRelativeTime(date: Date): string {
  const now = Date.now();
  const diffMs = now - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHr = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHr / 24);

  if (diffSec < 60) return 'just now';
  if (diffMin < 60) return `${diffMin} min ago`;
  if (diffHr < 24) return `${diffHr} hour${diffHr === 1 ? '' : 's'} ago`;
  return `${diffDay} day${diffDay === 1 ? '' : 's'} ago`;
}

/**
 * Generates a random alphanumeric ID.
 */
export function generateId(): string {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

/**
 * Creates a debounced version of the given function.
 * The function will only execute after `ms` milliseconds
 * have elapsed since the last invocation.
 */
export function debounce<T extends (...args: unknown[]) => void>(
  fn: T,
  ms: number,
): T {
  let timeoutId: ReturnType<typeof setTimeout>;

  const debounced = (...args: unknown[]) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), ms);
  };

  return debounced as T;
}
